import React, { useEffect, useState } from 'react';
import LoginArtBackground from '@navigation/screens/TopLevel/LoginScreen/Components/LoginArtBackground';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { loadLoginScreenStyles } from '@utils/loadWebStyles';
import {
  getLoginArtPhase,
  hideLoginArt,
  subscribeLoginArtPhase,
  type LoginArtPhase,
} from './loginArtTransition';
import {
  getGlassSplitPhase,
  isLoginGlassSplitEnabled,
  subscribeGlassSplitPhase,
  type GlassSplitPhase,
} from './loginGlassSplitTransition';

/** Hold at dimmed opacity before finishing the fade-out (no glass-split path). */
const DIM_HOLD_MS = 220;
/** Total time from dim start until art unmounts (no glass-split path). */
const DIM_TOTAL_MS = 920;

/**
 * When glass-split is active, keep art up until split starts fading / finishes,
 * so we don't uncover an empty Dashboard Suspense gap.
 */
function artExitDelays(glassPhase: GlassSplitPhase): { holdMs: number; totalMs: number } {
  if (!isLoginGlassSplitEnabled()) {
    return { holdMs: DIM_HOLD_MS, totalMs: DIM_TOTAL_MS };
  }
  if (glassPhase === 'fading') {
    return { holdMs: 0, totalMs: 360 };
  }
  if (glassPhase === 'splitting') {
    // Wait for Dashboard signal — safety aligned with glass max.
    return { holdMs: 4200, totalMs: 4600 };
  }
  return { holdMs: DIM_HOLD_MS, totalMs: DIM_TOTAL_MS };
}

/**
 * Web-only curtain for Login → Dashboard.
 * Login page art stays inside LoginScreen; this layer only mounts while dimming
 * so it can outlive the Login unmount from CommonActions.reset.
 */
export default function LoginArtShell() {
  const theme = themeSelector();
  const [phase, setPhase] = useState<LoginArtPhase>(getLoginArtPhase);
  const [glassPhase, setGlassPhase] = useState<GlassSplitPhase>(getGlassSplitPhase);
  const [exiting, setExiting] = useState(false);

  useEffect(() => subscribeLoginArtPhase(setPhase), []);
  useEffect(() => subscribeGlassSplitPhase(setGlassPhase), []);

  useEffect(() => {
    if (phase !== 'dimming') {
      setExiting(false);
      return;
    }

    loadLoginScreenStyles();
    document.documentElement.classList.add('login-art-transitioning');

    const { holdMs, totalMs } = artExitDelays(glassPhase);

    const holdTimer = window.setTimeout(() => setExiting(true), holdMs);
    const hideTimer = window.setTimeout(() => {
      hideLoginArt();
      document.documentElement.classList.remove('login-art-transitioning');
      setExiting(false);
    }, totalMs);

    return () => {
      window.clearTimeout(holdTimer);
      window.clearTimeout(hideTimer);
      document.documentElement.classList.remove('login-art-transitioning');
    };
  }, [phase, glassPhase]);

  // Curtain only — login screen owns the resting art.
  if (phase !== 'dimming') return null;

  const themeClass = theme ? 'login-root--light' : 'login-root--dark';
  const exitClass = exiting ? 'login-art-shell--exiting' : '';

  return (
    <div
      className={`login-art-shell ${themeClass} login-art-shell--dimming ${exitClass}`.trim()}
      aria-hidden
    >
      <LoginArtBackground />
    </div>
  );
}
